"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { Compass, Plus, X } from "lucide-react";
import { useEffect } from "react";

import { useToast } from "@/components/providers/FetchToastProvider";

import { useEstimator } from "../../_context/EstimatorContext";
import { useEstimatorChat } from "../../_context/EstimatorChatContext";
import { MAX_DESTINATIONS } from "../../_logic/constants";
import { placeRegion, type PlaceOption } from "../../_logic/places";
import { replaceDestination } from "../../_logic/replace-destination";
import { cityKey } from "../../_logic/workspace";
import { PlaceCombobox } from "./PlaceCombobox";

const REMOVE =
  "flex size-10 shrink-0 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

/**
 * The Where popover: one row per city to compare (up to five), each picked from real places and
 * editable, and a way to ask the assistant for cities worth comparing.
 * @param props.onAsk - Called before handing the question to the assistant, to close the popover.
 * @param props.focusCity - The city to focus on opening, e.g. the one the bar shows.
 * @returns The editor.
 * @throws Never.
 */
export const WhereEditor = ({
  onAsk,
  focusCity,
  draft,
  setDraft,
}: {
  onAsk: () => void;
  focusCity?: string | null;
  /** The new-city row's typed text, or null when there is no such row. Kept by the trip bar. */
  draft: string | null;
  setDraft: (draft: string | null) => void;
}) => {
  // Hooks
  const { showToast } = useToast();
  const { workspace, edit } = useEstimator();
  const { ask } = useEstimatorChat();

  // States
  const draftOpen = draft !== null;
  const setDraftOpen = (open: boolean) => setDraft(open ? "" : null);

  // Derived
  const full = workspace.destinations.length >= MAX_DESTINATIONS;

  // Handlers
  const pinAirport = (place: PlaceOption) =>
    place.codes[0] && edit({ type: "setAirport", city: place.city, code: place.codes[0] }, "user");
  const replace = (city: string, place: PlaceOption) => {
    const replacement = replaceDestination(workspace, city, place);
    if ("duplicate" in replacement) {
      showToast(`${place.city} is already in the comparison`, "warning");
      return;
    }
    replacement.actions.forEach((action) => edit(action, "user"));
  };
  const add = (place: PlaceOption) => {
    setDraftOpen(false);
    if (workspace.destinations.some((city) => cityKey(city) === cityKey(place.city))) return;
    edit({ type: "addDestination", city: place.city, region: placeRegion(place) }, "user");
    pinAirport(place);
  };

  // Effects
  useEffect(() => {
    if (draft === null && workspace.destinations.length === 0) setDraft("");
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render
  return (
    <>
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-gray-900">Cities to compare</h3>
        <p className="text-xs font-medium text-gray-600">
          Up to {MAX_DESTINATIONS}. Each gets live fares and hotel rates.
        </p>
      </div>
      <ul className="flex list-none flex-col gap-2">
        {workspace.destinations.map((city) => (
          <li key={cityKey(city)} className="flex items-start gap-2">
            <PlaceCombobox
              value={city}
              placeholder="Search a city"
              ariaLabel={`City to compare: ${city}`}
              behavior={{ autoFocus: focusCity ? cityKey(focusCity) === cityKey(city) : false }}
              onPick={(place) => replace(city, place)}
            />
            {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
            <button
              type="button"
              aria-label={`Remove ${city}`}
              onClick={() => edit({ type: "removeDestination", city }, "user")}
              className={REMOVE}
            >
              <X className="size-4" />
            </button>
          </li>
        ))}
        {draftOpen && !full && (
          <li className="flex items-start gap-2">
            <PlaceCombobox
              value={null}
              placeholder="Search a city"
              ariaLabel="City to compare"
              behavior={{
                autoFocus: true,
                draftText: draft ?? "",
                onDraftText: (text) => setDraft(text),
                onBackspaceEmpty: workspace.destinations.length ? () => setDraftOpen(false) : undefined,
              }}
              onPick={add}
            />
            {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
            <button type="button" aria-label="Cancel" onClick={() => setDraftOpen(false)} className={REMOVE}>
              <X className="size-4" />
            </button>
          </li>
        )}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        {!draftOpen && !full && (
          <FetchButton variant="text" size="sm" color="primary" type="button" onClick={() => setDraftOpen(true)}>
            <Plus className="size-4" />
            Add city
          </FetchButton>
        )}
        <FetchButton
          variant="outline"
          size="sm"
          color="neutral"
          type="button"
          disabled={full}
          onClick={() => {
            onAsk();
            ask(
              "Suggest cities",
              workspace.destinations.length
                ? "Suggest a few more cities worth comparing for our group."
                : "We don't know where to go yet. Suggest cities for our group."
            );
          }}
        >
          <Compass className="size-4" />
          Suggest cities
        </FetchButton>
        {full && (
          <span className="text-xs font-medium text-gray-600">
            Up to {MAX_DESTINATIONS} cities. Remove one to add or suggest another.
          </span>
        )}
      </div>
    </>
  );
};
